/**
 * The RBAC catalogue and the client-side permission matcher.
 *
 * Lives in the leaf layer, not in `features/access`, so any feature can gate its
 * UI on a permission (`app -> features -> leaves`, see eslint's
 * `import/no-restricted-paths`).
 */

/** A resource/action pair a Role confers on its holders. */
export interface GrantedPermission {
  resource: string;
  action: string;
}

/**
 * One Grant as the user listing previews it: the scope and the Role it names,
 * without the permissions behind it (admin-api `RoleGrantSummaryResponse`). A row
 * of `GET /v1/users` must not pay for a permission preload; the single-user read
 * is what expands them.
 *
 * `game_code` / `sandbox_code` are '*' for a wildcard scope, matching the
 * backend's stored scope exactly.
 *
 * Carries no role id: the wire names the Role, it does not identify it. Revoking
 * needs the UUID, which is resolved by name from `GET /v1/roles`.
 */
export interface RoleGrantSummary {
  role: string;
  description: string;
  game_code: string;
  sandbox_code: string;
}

/** One Grant held by a user, with the permissions it carries. */
export interface MyRoleGrant extends RoleGrantSummary {
  permissions: GrantedPermission[];
}

// The permissions we ship, as `resource:action` codes. Mirrors the RBAC Design
// Document's capability catalog and the admin-api seed migration
// 20260702134918_seed_rbac_catalog.sql, plus the later
// 20260901083305_add_import_permissions.sql. Wildcard rows ('*') exist only on the Grant
// side — the UI always asks for a concrete permission.
//
// Entries marked "reserved" govern no endpoint today: granting them changes nothing,
// so never gate a control on one. `beacons:send` is bundled by no standard Role, so
// only `administrator` holds it.
const StorePermission = {
  read: 'store:read',
  create: 'store:create',
  edit: 'store:edit',
  delete: 'store:delete',
  publish: 'store:publish',
  /** POST /v1/store/snapshots/import. Not implied by create (BR-RBAC-36). */
  import: 'store:import',
} as const;

const ItemsPermission = {
  read: 'items:read',
  create: 'items:create',
  edit: 'items:edit',
  /** Reserved — governs no endpoint today. */
  delete: 'items:delete',
  /**
   * POST /v1/items/import, which is also how bundles and currencies are imported
   * (BR-RBAC-34) — there is no bundles:import. Not implied by create (BR-RBAC-36).
   */
  import: 'items:import',
} as const;

/** Reserved in full — no bundle endpoint exists today. */
const BundlesPermission = {
  read: 'bundles:read',
  create: 'bundles:create',
  edit: 'bundles:edit',
  delete: 'bundles:delete',
} as const;

const GiftsPermission = {
  read: 'gifts:read',
  create: 'gifts:create',
  edit: 'gifts:edit',
  delete: 'gifts:delete',
  /** POST /v1/gifts/import. Not implied by create (BR-RBAC-36). */
  import: 'gifts:import',
} as const;

const SeasonPassPermission = {
  read: 'seasonpass:read',
  create: 'seasonpass:create',
  edit: 'seasonpass:edit',
  delete: 'seasonpass:delete',
  /** POST /v1/season-passes/import. Not implied by create (BR-RBAC-36). */
  import: 'seasonpass:import',
} as const;

// Bans apply to the whole Game: check them with scope 'game', not 'sandbox'.
const BansPermission = {
  /** Reserved — governs no endpoint today. */
  read: 'bans:read',
  create: 'bans:create',
  edit: 'bans:edit',
  /** Reserved — governs no endpoint today. */
  delete: 'bans:delete',
} as const;

const SandboxesPermission = {
  read: 'sandboxes:read',
  /** Game-level: whoever can create a Sandbox can create them anywhere in the Game. */
  create: 'sandboxes:create',
  edit: 'sandboxes:edit',
  delete: 'sandboxes:delete',
  publish: 'sandboxes:publish',
} as const;

const TranslationsPermission = {
  /**
   * Reserved — no route enforces it (admin-api `internal/api/http/router.go`).
   * Translations are only ever read as an enrichment on the item endpoints, so
   * reading them needs `items:read`, and the language registry `languages:read`.
   */
  read: 'translations:read',
  /** POST /v1/items/translations, dry run included. */
  edit: 'translations:edit',
} as const;

const LanguagesPermission = {
  read: 'languages:read',
  edit: 'languages:edit',
} as const;

const InventoryPermission = {
  read: 'inventory:read',
  grant: 'inventory:grant',
} as const;

const PlayersPermission = { read: 'players:read' } as const;

// The Nacon Live user directory. Checked at global scope by admin-api
// (router.go, ScopeGlobal), so only a ('*', '*') Grant satisfies it: a
// Game-scoped Permission Manager gets a 403.
const UsersPermission = { read: 'users:read' } as const;

const BeaconsPermission = { send: 'beacons:send' } as const;

// Granting/revoking a Role is checked on the scope being granted; editing a Role
// definition and reading the audit log need this at global scope.
const AdminPermission = {
  managePermissions: 'admin:manage_permissions',
} as const;

/**
 * Every resource and the actions it supports, keyed by the backend's resource name —
 * the single entry point for the codes: `Permissions.players.read`,
 * `Permissions.admin.managePermissions`. Also what wildcard expansion reads.
 */
export const Permissions = {
  admin: AdminPermission,
  bans: BansPermission,
  beacons: BeaconsPermission,
  bundles: BundlesPermission,
  gifts: GiftsPermission,
  inventory: InventoryPermission,
  items: ItemsPermission,
  languages: LanguagesPermission,
  players: PlayersPermission,
  sandboxes: SandboxesPermission,
  seasonpass: SeasonPassPermission,
  store: StorePermission,
  translations: TranslationsPermission,
  users: UsersPermission,
} as const;

type Values<T> = T[keyof T];

/** Every code in the catalogue above — derived from it, so it cannot drift. */
export type PermissionCode = Values<{
  [Resource in keyof typeof Permissions]: Values<
    (typeof Permissions)[Resource]
  >;
}>;

/**
 * The scope wildcard, on either side of a Grant's (game, sandbox) pair and on either
 * side of a permission's resource:action. Stored as-is by the backend.
 */
export const WILDCARD = '*';

/** A '*' on the granted side widens it; an omitted request leaves it unconstrained. */
const covers = (granted: string, requested?: string) =>
  requested === undefined || granted === WILDCARD || granted === requested;

/**
 * Whether one Grant satisfies `code` in the given scope. Wildcards widen a Grant the
 * same way the backend matches them: `game_code IN (requested, '*') AND sandbox_code
 * IN (requested, '*')`. An omitted scope segment is unconstrained — a page with no
 * :sandboxCode in its route accepts a Grant scoped to any single Sandbox.
 */
export const grantCovers = (
  grant: MyRoleGrant,
  code: PermissionCode,
  gameCode?: string,
  sandboxCode?: string,
) => {
  const [resource, action] = code.split(':');

  return (
    covers(grant.game_code, gameCode) &&
    covers(grant.sandbox_code, sandboxCode) &&
    grant.permissions.some(
      (permission) =>
        covers(permission.resource, resource) &&
        covers(permission.action, action),
    )
  );
};

const actionsOf = (resource: string) =>
  resource in Permissions
    ? Object.values(Permissions[resource as keyof typeof Permissions]).map(
        (code) => code.split(':')[1] ?? code,
      )
    : [];

/**
 * Replaces wildcards with the concrete permissions they cover, for display: '*:*'
 * becomes every permission in the catalogue, 'store:*' every store action. A
 * resource the catalogue doesn't know passes through untouched, so a permission the
 * backend adds still shows up.
 */
export const expandPermissions = (
  permissions: GrantedPermission[],
): GrantedPermission[] => {
  const expanded = permissions.flatMap(({ resource, action }) =>
    (resource === WILDCARD ? Object.keys(Permissions) : [resource]).flatMap(
      (expandedResource) => {
        if (action !== WILDCARD)
          return [{ resource: expandedResource, action }];
        const actions = actionsOf(expandedResource);
        return actions.length > 0
          ? actions.map((expandedAction) => ({
              resource: expandedResource,
              action: expandedAction,
            }))
          : [{ resource: expandedResource, action }];
      },
    ),
  );

  // A Role can hold both 'store:*' and 'store:read'; show each permission once.
  return [
    ...new Map(
      expanded.map((permission) => [
        `${permission.resource}:${permission.action}`,
        permission,
      ]),
    ).values(),
  ];
};

/**
 * Whether any Grant covers this (Game, Sandbox) at all, whatever it permits —
 * "may this user enter this Sandbox", which no single code answers: a Role scoped
 * to one Sandbox opens it whatever its permissions, and `sandboxes:read` is held
 * by almost no Standard Role.
 */
export const coversScope = (
  grants: MyRoleGrant[],
  gameCode?: string,
  sandboxCode?: string,
) =>
  grants.some(
    (grant) =>
      covers(grant.game_code, gameCode) &&
      covers(grant.sandbox_code, sandboxCode),
  );

/** Whether the signed-in user holds `code` on (gameCode, sandboxCode). */
export const hasPermission = (
  grants: MyRoleGrant[],
  code: PermissionCode,
  gameCode?: string,
  sandboxCode?: string,
) => grants.some((grant) => grantCovers(grant, code, gameCode, sandboxCode));

/**
 * The three scope levels of the RBAC Design Document. Most capabilities are checked
 * on the current Game *and* Sandbox; a few are Game-level whatever the Sandbox
 * (`bans:create`, `bans:edit`, `sandboxes:create`); editing Role definitions and
 * reading the audit log are global.
 */
export type PermissionScope = 'sandbox' | 'game' | 'global';

interface PermissionScopeOptions {
  /**
   * Defaults to 'sandbox'. Note that 'sandbox' on a route without a :sandboxCode
   * degrades to Game level — that is what keeps the Access area open to
   * Sandbox-scoped permission admins.
   */
  scope?: PermissionScope;
  /** Overrides the route's :gameCode. Ignored when scope is 'global'. */
  gameCode?: string;
  /** Overrides the route's :sandboxCode. Only used when scope is 'sandbox'. */
  sandboxCode?: string;
}

// At least one of oneOf / allOf is required: an empty check must never pass.
export type PermissionCheckOptions = PermissionScopeOptions &
  (
    | { oneOf: PermissionCode[]; allOf?: PermissionCode[] }
    | { oneOf?: PermissionCode[]; allOf: PermissionCode[] }
  );

/**
 * Whether `grants` satisfy `check` on the scope `params` describe. The single
 * evaluation shared by the component gate (`usePermissionCheck`) and the route gate
 * (`requirePermissions`), so the sidebar and the loaders cannot disagree.
 *
 * `oneOf` and `allOf` can be combined — both must then be satisfied.
 */
export const holdsPermissions = (
  grants: MyRoleGrant[],
  {
    oneOf,
    allOf,
    scope = 'sandbox',
    gameCode,
    sandboxCode,
  }: PermissionCheckOptions,
  params: { gameCode?: string; sandboxCode?: string } = {},
): boolean => {
  // 'global' asks for the (*, *) Grant the platform admins hold: '*' on the requested
  // side only matches a '*' Grant. 'game' leaves the Sandbox unconstrained, so a Grant
  // on any single Sandbox of the Game satisfies it.
  const game = scope === 'global' ? WILDCARD : (gameCode ?? params.gameCode);
  const sandbox =
    scope === 'global'
      ? WILDCARD
      : scope === 'game'
        ? undefined
        : (sandboxCode ?? params.sandboxCode);

  const held = (code: PermissionCode) =>
    hasPermission(grants, code, game, sandbox);

  return (oneOf?.some(held) ?? true) && (allOf?.every(held) ?? true);
};
